import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { BUTTON_HTML_TYPE } from 'constants/button'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import useDelayUnmount from 'components/custom-hooks/useDelayUnmount'
import Icon from '../icon'
import ICONS, { ICON_SIZE } from '../icon/const'
import styles from './style.scss'
import Badge from '../badge'
import { BADGE_COLOR } from '../badge/const'

/**
 * @param {string} param0.id                       element id
 * @param {string | number | element} param0.title The text that display on the top of accordion
 * @param {boolean} param0.isExpanded              flag to set accordion to be expand/collapse
 * @param {function} param0.onChange               event callback when expand/collapse was changed
 * @param {element} param0.children                the content inside accordion.
 * @param {function} param0.onClick                function to override the expand behavior
 * @param {boolean} param0.isSizeGuide             flag to set new size or not
 * @param {function} param0.t
 */
const Accordion = ({
  id,
  children,
  title,
  titleTypography,
  isExpanded,
  onChange,
  withArrow,
  onClick,
  isSizeGuide,
  isSizeUpdated,
  t,
}) => {
  const {
    state: visible,
    delayState: delayVisible,
    setState: setVisible,
  } = useDelayUnmount(isExpanded, 240)
  const header = useMemo(() => {
    if (['number', 'string'].includes(typeof title)) {
      return (
        <div className="header-container">
          <span
            style={{ marginRight: '10px' }}
            className={clsx(
              'accordion__header-text',
              titleTypography || Typo.body2,
            )}
          >
            {title}
          </span>
          {isSizeUpdated ? (
            <Badge
              color={BADGE_COLOR.secondary}
              contentClassName={clsx(Typo.overline)}
              content={hasLocizeTranslation(t, 'PDP_SIZE_UPDATED', 'UPDATED')}
            />
          ) : (
            isSizeGuide && (
              <Badge
                color={BADGE_COLOR.secondary}
                contentClassName={clsx(Typo.overline)}
                content={hasLocizeTranslation(t, 'PDP_SIZE_ADDED', 'NEW SIZES')}
              />
            )
          )}
        </div>
      )
    }
    return title
  }, [t, title, titleTypography, isSizeGuide, isSizeUpdated])

  const handleClickHeader = useCallback(() => {
    setVisible((prevVisible) => {
      const nextVisible = !prevVisible
      onChange?.(nextVisible)
      return nextVisible
    })
  }, [onChange, setVisible])

  useEffect(() => {
    setVisible(isExpanded)
  }, [setVisible, isExpanded])

  return (
    <div
      id={id}
      className={clsx('accordion__container', { collapse: !visible })}
    >
      <style jsx>{styles}</style>
      <button
        onClick={onClick || handleClickHeader}
        type={BUTTON_HTML_TYPE.button}
        className="accordion__header webview-cta"
      >
        {header}
        <div className="accordion__header-icon">
          {withArrow ? (
            <Icon
              size={ICON_SIZE.smallMedium}
              src={ICONS.dropupSmall}
              className={clsx({ 'arrow-down': !visible })}
            />
          ) : (
            <Icon
              size={ICON_SIZE.smallMedium}
              src={visible ? ICONS.minus : ICONS.plus}
            />
          )}
        </div>
      </button>
      {(visible || delayVisible) && (
        <div
          className={clsx('accordion__content', {
            '--active': visible && delayVisible,
          })}
          data-cy="pml-accordion-content"
        >
          {children}
        </div>
      )}
    </div>
  )
}

Accordion.propTypes = {
  id: PropTypes.string,
  title: PropTypes.node,
  titleTypography: PropTypes.string,
  children: PropTypes.element,
  isExpanded: PropTypes.bool,
  withArrow: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  isSizeGuide: PropTypes.bool,
  t: PropTypes.func.isRequired,
  isSizeUpdated: PropTypes.bool,
}

Accordion.defaultProps = {
  id: undefined,
  title: '',
  titleTypography: undefined,
  isExpanded: false,
  withArrow: false,
  children: undefined,
  onChange: undefined,
  onClick: undefined,
  isSizeGuide: false,
  isSizeUpdated: false,
}

export default withI18next()(Accordion)
